/**
 * Notification Preferences Panel (Phase 3 — Step 3.8).
 *
 * Per-course alert configuration, custom milestones, quiet hours, DND.
 */

import React, { useState } from 'react';
import { ScrubInput } from 'calc-controls';
import { useAppStore } from '@/store';
import type { NotificationPreferences, Course } from '@abs/shared-types';
import { CollapsiblePanel } from '../shared';

interface NotificationPreferencesPanelProps {
  course: Course;
}

const DEFAULT_PREFS: NotificationPreferences = {
  courseId: '',
  proximityAlerts: true,
  milestoneAlerts: true,
  consequenceAlerts: true,
  paceAlerts: true,
  customMilestones: [],
  quietHoursStart: null,
  quietHoursEnd: null,
  doNotDisturb: false,
};

export function NotificationPreferencesPanel({ course }: NotificationPreferencesPanelProps): React.ReactElement {
  const settings = useAppStore((s) => s.settings);
  const updateSettings = useAppStore((s) => s.updateSettings);

  // Store prefs in user settings as a map keyed by courseId
  const prefsMap = settings.notificationPrefs;
  const prefs: NotificationPreferences = {
    ...DEFAULT_PREFS,
    courseId: course.id,
    ...(prefsMap?.[course.id] ?? {}),
  };

  const [quietStart, setQuietStart] = useState(prefs.quietHoursStart ?? '');
  const [quietEnd, setQuietEnd] = useState(prefs.quietHoursEnd ?? '');
  const [customMilestonePct, setCustomMilestonePct] = useState<number>(50);

  const save = (updates: Partial<NotificationPreferences>): void => {
    const newPrefs = { ...prefs, ...updates };
    const updatedMap = { ...(prefsMap ?? {}), [course.id]: newPrefs };
    updateSettings({
      ...settings,
      notificationPrefs: updatedMap,
    });
  };

  const addMilestone = (): void => {
    const val = customMilestonePct / 100;
    if (prefs.customMilestones.includes(val)) return;
    save({ customMilestones: [...prefs.customMilestones, val].sort((a, b) => a - b) });
  };

  const removeMilestone = (val: number): void => {
    save({ customMilestones: prefs.customMilestones.filter((m) => m !== val) });
  };

  return (
    <CollapsiblePanel title="Notification Preferences" defaultOpen={false}>
      <div className="notification-prefs">
        <label className="notification-prefs__toggle">
          <input
            type="checkbox"
            checked={prefs.doNotDisturb}
            onChange={(e) => save({ doNotDisturb: e.target.checked })}
          />
          Do Not Disturb (suppress all alerts)
        </label>

        {!prefs.doNotDisturb && (
          <>
            <div className="notification-prefs__section">
              <h5>Alert Types</h5>
              <label className="notification-prefs__toggle">
                <input
                  type="checkbox"
                  checked={prefs.proximityAlerts}
                  onChange={(e) => save({ proximityAlerts: e.target.checked })}
                />
                Proximity alerts (near next tier)
              </label>
              <label className="notification-prefs__toggle">
                <input
                  type="checkbox"
                  checked={prefs.milestoneAlerts}
                  onChange={(e) => save({ milestoneAlerts: e.target.checked })}
                />
                Milestone alerts (50%, 75%, 90% consumed)
              </label>
              <label className="notification-prefs__toggle">
                <input
                  type="checkbox"
                  checked={prefs.consequenceAlerts}
                  onChange={(e) => save({ consequenceAlerts: e.target.checked })}
                />
                Consequence alerts (what happens next)
              </label>
              <label className="notification-prefs__toggle">
                <input
                  type="checkbox"
                  checked={prefs.paceAlerts}
                  onChange={(e) => save({ paceAlerts: e.target.checked })}
                />
                Pace warnings (miss rate trending to fail)
              </label>
            </div>

            <div className="notification-prefs__section">
              <h5>Quiet Hours</h5>
              <div className="notification-prefs__quiet">
                <label>
                  From
                  <input
                    type="time"
                    value={quietStart}
                    onChange={(e) => {
                      setQuietStart(e.target.value);
                      save({ quietHoursStart: e.target.value || null });
                    }}
                  />
                </label>
                <label>
                  To
                  <input
                    type="time"
                    value={quietEnd}
                    onChange={(e) => {
                      setQuietEnd(e.target.value);
                      save({ quietHoursEnd: e.target.value || null });
                    }}
                  />
                </label>
              </div>
            </div>

            <div className="notification-prefs__section">
              <h5>Custom Milestones</h5>
              <div className="notification-prefs__milestones">
                {prefs.customMilestones.map((m) => (
                  <span key={m} className="notification-prefs__milestone-tag">
                    {Math.round(m * 100)}%
                    <button type="button" onClick={() => removeMilestone(m)} aria-label={`Remove ${m * 100}% milestone`}>&times;</button>
                  </span>
                ))}
              </div>
              <div className="notification-prefs__add-milestone">
                <ScrubInput
                  label="Custom milestone (%)"
                  value={customMilestonePct}
                  onChange={setCustomMilestonePct}
                  min={1}
                  max={99}
                  decimals={0}
                  unit="%"
                  inline
                />
                <button type="button" onClick={addMilestone}>Add</button>
              </div>
            </div>
          </>
        )}
      </div>
    </CollapsiblePanel>
  );
}
